import React, { useRef } from 'react';
import {
  UserStatusDropdown,
  UserStatusDropdownButton,
  UserStatusDropdownListButton,
  UserStatusDropdownMenu,
} from './UserStatusMenu.styled';
import { BodyText, Icon, Spinner, Stack } from '@servicetitan/design-system';
import useUserStatus from '@modules/UserStatusModule/hooks/useUserStatus';
import useOnClickOutside from '@lib/hooks/useOnClickOutside';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import UserStatusModule from '@modules/UserStatusModule';
import UserStatusDisplay from './UserStatusDisplay';
import useCountdownTimer from '@lib/hooks/useCountDownTimer';
import { UserSetAvailabilityStatus } from '@api/generated/graphql';
import statusValues, {
  StatusDotColor,
} from '@modules/UserStatusModule/statusValues';
import { ITask } from '@modules/TasksModule/types';

type UserStatusMenuProps = {
  furthestWrapUpTask?: ITask;
};

/** Displays and updates the status of the current user via a dropdown like menu */
export function UserStatusMenu({ furthestWrapUpTask }: UserStatusMenuProps) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const { status: userStatus, loading, error, canChange } = useUserStatus();
  const disabled = loading || !!error || !canChange;

  const clickOutsideRef = useRef<HTMLDivElement>(null);
  const clickOutsideIgnoreRef = useRef<HTMLButtonElement>(null);
  useOnClickOutside(
    clickOutsideRef,
    () => setOpen(false),
    clickOutsideIgnoreRef,
  );

  const { count } = useCountdownTimer(furthestWrapUpTask?.wrapUpEndsAt || 0);

  const status =
    furthestWrapUpTask && count > 0 ? 'Extended Wrap Up' : userStatus;

  const options = Object.entries(statusValues).map(([value, text]) => ({
    text,
    value,
  }));

  return (
    <UserStatusDropdown>
      <UserStatusDropdownButton
        disabled={disabled}
        $status={status}
        onClick={() => {
          setOpen(!open);
        }}
        ref={clickOutsideIgnoreRef}
      >
        <Stack justifyContent='space-around' alignItems='center'>
          <Stack.Item fill>
            <Stack alignItems='center'>
              <Icon
                className='m-r-half'
                name='shape_circle'
                color={StatusDotColor[status]}
                el='span'
                size={16}
              />
              <UserStatusDisplay
                status={userStatus}
                wrapUpTimeInSeconds={count}
              />
            </Stack>
          </Stack.Item>
          {/* display expand icons if not loading */}
          {loading ? (
            <Spinner color={StatusDotColor[status]} el='span' />
          ) : open ? (
            <Icon name='expand_less' color={StatusDotColor[status]} el='span' />
          ) : (
            <Icon name='expand_more' color={StatusDotColor[status]} el='span' />
          )}
        </Stack>
      </UserStatusDropdownButton>
      <UserStatusDropdownMenu
        className='p-1 m-0'
        open={open}
        ref={clickOutsideRef}
      >
        <Stack
          direction='column'
          spacing={1}
          alignItems='center'
          justifyContent='center'
        >
          {options.map((option) => (
            <UserStatusDropdownListButton
              className='p-y-half p-x-1'
              $status={option.value}
              key={option.value}
              onClick={() => {
                UserStatusModule.setUserStatus(
                  user?.id || '',
                  option.value as UserSetAvailabilityStatus,
                );
                setOpen(false);
              }}
              disabled={disabled}
            >
              <Stack alignItems='center'>
                <Icon
                  className='m-r-half'
                  name='shape_circle'
                  color={StatusDotColor[option.value]}
                  el='span'
                />
                <BodyText className='m-0' size='xsmall'>
                  {option.text}
                </BodyText>
              </Stack>
            </UserStatusDropdownListButton>
          ))}
        </Stack>
      </UserStatusDropdownMenu>
    </UserStatusDropdown>
  );
}
